import { Alert, Box, Button, Divider, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Tooltip, Typography } from "@mui/material"
import { useContext, useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import moment				from 'moment'

import HelpOutlineIcon		from '@mui/icons-material/HelpOutline'
import DeleteIcon 			from '@mui/icons-material/Delete'
import SaveIcon 			from '@mui/icons-material/Save'
import CancelIcon 			from '@mui/icons-material/Cancel'
import EditIcon				from '@mui/icons-material/EditOutlined'

import CompanyContext		from "../context"
import useFetch 			from "../../../api/api"
import useBreakpoint 		from "../../../hooks/useBreakpoint"

import Loading 				from "../../../components/Loading"
import ConfirmDialog 		from "../../../components/ConfirmDialog"
import config				from "../../../common/Config"

const ACTION_OPTIONS = [
	{ value: '',         label: 'Sem decisão' },
	{ value: 'analisar', label: 'Analisar'    },
	{ value: 'comprar',  label: 'Comprar'     },
	{ value: 'manter',   label: 'Stand-by'    },
	{ value: 'vender',   label: 'Vender'      },
]

const PERIOD_ORDER  = { FY: 0, Q4: 1, Q3: 2, Q2: 3, Q1: 4 }
const PERIOD_LABELS = { FY: 'Anual', Q1: '1T', Q2: '2T', Q3: '3T', Q4: '4T' }

const formatPeriodLabel = (year, period) => `${year} - ${PERIOD_LABELS[period] ?? period}`

const buildPeriodOptions = (company) => {
	if (!company) return []
	const annual    = (company.financialReports || [])
		.filter(r => r.period !== 'LTM')
		.map(r => ({ value: `${r.year}|${r.period}`, label: formatPeriodLabel(r.year, r.period), year: r.year, period: r.period }))
	const quarterly = (company.financialQuarterReports || [])
		.map(r => ({ value: `${r.year}|${r.period}`, label: formatPeriodLabel(r.year, r.period), year: r.year, period: r.period }))
	return [...annual, ...quarterly].sort((a, b) => {
		if (a.year !== b.year) return b.year - a.year
		return (PERIOD_ORDER[a.period] ?? 99) - (PERIOD_ORDER[b.period] ?? 99)
	})
}

const PanelReviews = () => {

	const { company, portfolioVersion }				= useContext(CompanyContext)
	const { ticker }								= useParams()
	const privateFetch								= useFetch()

	const [isLoading, setIsLoading]					= useState(true)

	// Racional
	const [rationalId, setRationalId]					= useState(null)
	const [rational, setRational]						= useState(null)
	const [rationalDraft, setRationalDraft]				= useState('')
	const [isEditingRational, setIsEditingRational]		= useState(false)
	const [rationalError, setRationalError]				= useState('')
	const [isDeleteRationalOpen, setIsDeleteRationalOpen] = useState(false)

	// Portfólio
	const [inPortfolio, setInPortfolio]				= useState(false)
	const [portfolioAction, setPortfolioAction]		= useState('')
	const [actionError, setActionError]				= useState('')
	const [periodError, setPeriodError]				= useState('')

	// Reviews periódicas
	const [reviews, setReviews]						= useState([])
	const [newReviewText, setNewReviewText]			= useState('')
	const [selectedPeriod, setSelectedPeriod]		= useState('')

	const periodOptions = buildPeriodOptions(company)

	useEffect(() => {
		if (periodOptions.length && !selectedPeriod) {
			setSelectedPeriod(periodOptions[0].value)
		}
	}, [company])

	useEffect(() => {
		if (!company) return
		privateFetch(config.apiUrl + '/api/review/' + company.id)
			.then(r => r.json())
			.then(data => {
				const rat = data.find(d => d.type === 'Rational')
				setRationalId(rat?.id ?? null)
				setRational(rat?.text ?? null)
				setReviews(data.filter(d => d.type !== 'Rational'))
				setIsLoading(false)
			})
	}, [company, isLoading])

	useEffect(() => {
		if (!ticker) return
		privateFetch(config.apiUrl + '/api/portfolio/action/' + ticker)
			.then(r => r.json())
			.then(r => {
				if (r.status === 200) {
					setInPortfolio(r.data.inPortfolio === true)
					setPortfolioAction(r.data.action ?? '')
					setActionError('')
				} else {
					setInPortfolio(false)
				}
			})
			.catch(() => {})
	}, [ticker, portfolioVersion])

	const handleActionChange = (newAction) => {
		if (!inPortfolio) {
			setActionError('Adicione a empresa ao portfólio para definir uma decisão.')
			return
		}
		setActionError('')
		setPortfolioAction(newAction)
		privateFetch(config.apiUrl + '/api/portfolio/action', {
			method	: 'PATCH',
			headers	: { 'content-type': 'application/json' },
			body	: JSON.stringify({ ticker, action: newAction || null })
		})
	}

	const handleSaveRational = async () => {
		if (!rationalDraft.trim()) {
			setRationalError('Escreva o racional antes de salvar.')
			return
		}
		setRationalError('')
		await privateFetch(config.apiUrl + '/api/review', {
			method	: 'PUT',
			headers	: { 'Content-Type': 'application/json' },
			body	: JSON.stringify({ companyId: company.id, id: rationalId, type: 'Rational', text: rationalDraft })
		})
		setIsEditingRational(false)
		setIsLoading(true)
	}

	const handleDeleteRational = async () => {
		await privateFetch(config.apiUrl + '/api/review/' + rationalId, {
			method	: 'DELETE',
			headers	: { 'Content-Type': 'application/json' }
		})
		setIsLoading(true)
	}

	const handleStartEditRational = () => {
		setRationalDraft(rational ?? '')
		setRationalError('')
		setIsEditingRational(true)
	}

	const handleSaveReview = async () => {
		if (reviews.some(r => `${r.year}|${r.period}` === selectedPeriod)) {
			setPeriodError('Já existe uma análise para este período.')
			return
		}
		setPeriodError('')
		const opt = periodOptions.find(p => p.value === selectedPeriod)
		await privateFetch(config.apiUrl + '/api/review', {
			method	: 'PUT',
			headers	: { 'Content-Type': 'application/json' },
			body	: JSON.stringify({ companyId: company.id, id: null, text: newReviewText, year: opt?.year, period: opt?.period })
		})
		setNewReviewText('')
		setIsLoading(true)
	}

	const handleEditReview = async (reviewId, companyId, reviewText, year, period) => {
		await privateFetch(config.apiUrl + '/api/review', {
			method	: 'PUT',
			headers	: { 'Content-Type': 'application/json' },
			body	: JSON.stringify({ companyId, id: reviewId, text: reviewText, year, period })
		})
		setIsLoading(true)
	}

	const handleDeleteReview = async (reviewId) => {
		await privateFetch(config.apiUrl + '/api/review/' + reviewId, {
			method	: 'DELETE',
			headers	: { 'Content-Type': 'application/json' }
		})
		setIsLoading(true)
	}

	if (isLoading) return <Loading/>

	const ActionSelect = () => (
		<FormControl size="small" sx={{ minWidth: 200 }}>
			<InputLabel>Decisão no portfólio</InputLabel>
			<Select
				label="Decisão no portfólio"
				value={portfolioAction}
				onChange={(e) => handleActionChange(e.target.value)}
			>
				{ACTION_OPTIONS.map(opt => (
					<MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
				))}
			</Select>
		</FormControl>
	)

	return (
		<Box sx={{ pt: 1, paddingX: 2, marginBottom: 5 }}>

			{/* ── Racional ── */}
			<Box mb={2}>
				<Box display="flex" alignItems="center" mb={1}>
					<Typography variant="h6">Racional de Investimento</Typography>
					<Tooltip sx={{ pl: 1 }} title={<Typography>Descreva aqui a sua análise sobre a empresa e as motivações para investir ou não nesta empresa.</Typography>}>
						<HelpOutlineIcon fontSize="small" sx={{ ml: 0.5 }} />
					</Tooltip>
				</Box>

				<ConfirmDialog
					title="Excluir racional?"
					open={isDeleteRationalOpen}
					setOpen={setIsDeleteRationalOpen}
					onConfirm={handleDeleteRational}
				>
					Tem certeza que deseja excluir o racional de investimento?
				</ConfirmDialog>

				{isEditingRational || !rational ? (
					<Stack spacing={1.5}>
						<TextField
							multiline
							fullWidth
							rows={4}
							value={rationalDraft}
							onChange={(e) => { setRationalDraft(e.target.value); setRationalError('') }}
							placeholder="Escreva seu racional de investimento..."
							error={!!rationalError}
						/>
						{rationalError && <Alert severity="error" sx={{ py: 0 }}>{rationalError}</Alert>}
						{actionError && <Alert severity="error" sx={{ py: 0 }} onClose={() => setActionError('')}>{actionError}</Alert>}
						<Box display="flex" alignItems="center" justifyContent="space-between">
							<ActionSelect />
							<Stack direction="row" spacing={1}>
								{rational && (
									<Button variant="outlined" onClick={() => setIsEditingRational(false)}>Cancelar</Button>
								)}
								<Button variant="contained" onClick={handleSaveRational}>Salvar</Button>
							</Stack>
						</Box>
					</Stack>
				) : (
					<Stack spacing={1.5}>
						<Typography sx={{ whiteSpace: 'pre-wrap' }}>{rational}</Typography>
						{actionError && <Alert severity="error" sx={{ py: 0 }} onClose={() => setActionError('')}>{actionError}</Alert>}
						<Box display="flex" alignItems="center" justifyContent="space-between">
							<ActionSelect />
							<Stack direction="row" spacing={1}>
								<Button variant="contained" color="error" onClick={() => setIsDeleteRationalOpen(true)}><DeleteIcon /></Button>
								<Button variant="contained" startIcon={<EditIcon />} onClick={handleStartEditRational}>Editar</Button>
							</Stack>
						</Box>
					</Stack>
				)}
			</Box>

			<Divider sx={{ my: 2 }} />

			{/* ── Nova análise periódica ── */}
			<Box mb={2}>
				<Box display="flex" alignItems="center" mb={1}>
					<Typography variant="h6">Análises</Typography>
					<Tooltip sx={{ pl: 1 }} title={<Typography>Anote aqui suas avaliações periódicas da companhia. Acompanhe a evolução e anote eventuais outlooks divulgados para verificar se eles foram atingidos.</Typography>}>
						<HelpOutlineIcon fontSize="small" sx={{ ml: 0.5 }} />
					</Tooltip>
				</Box>
				<Stack spacing={1.5} alignItems="flex-end">
					<FormControl fullWidth size="small">
						<InputLabel>Resultado</InputLabel>
						<Select
							label="Resultado"
							value={selectedPeriod}
							onChange={(e) => setSelectedPeriod(e.target.value)}
						>
							{periodOptions.map(opt => (
								<MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
							))}
						</Select>
					</FormControl>
					<TextField
						label="Análise"
						multiline
						fullWidth
						rows={4}
						value={newReviewText}
						onChange={(e) => setNewReviewText(e.target.value)}
					/>
					{periodError && (
						<Alert severity="error" sx={{ py: 0, alignSelf: 'stretch' }} onClose={() => setPeriodError('')}>
							{periodError}
						</Alert>
					)}
					<Button
						variant="contained"
						sx={{ width: 100 }}
						onClick={handleSaveReview}
					>
						Salvar
					</Button>
				</Stack>
			</Box>

			{/* ── Lista de análises anteriores ── */}
			<Stack direction="column" spacing={2} mt={1} alignItems="flex-end">
				{reviews.map(r => (
					<ReviewItem key={r.id} review={r} periodOptions={periodOptions} onSave={handleEditReview} onDelete={handleDeleteReview} />
				))}
			</Stack>
		</Box>
	)
}

const ReviewItem = ({ review, onSave, onDelete, periodOptions }) => {

	const [isEditing, setIsEditing]						= useState(false)
	const [content, setContent]							= useState(review.text)
	const [isDeleteConfirmOpen, setIsDeleteConfirmOpen]	= useState(false)
	const [editPeriod, setEditPeriod]					= useState(
		review.year && review.period ? `${review.year}|${review.period}` : (periodOptions[0]?.value || '')
	)
	const currentBreakPoint								= useBreakpoint()

	const handleSave = () => {
		const opt = periodOptions.find(p => p.value === editPeriod)
		if (typeof onSave === 'function') onSave(review.id, review.companyId, content, opt?.year, opt?.period)
		setIsEditing(false)
	}

	const handleDelete = () => {
		if (typeof onDelete === 'function') onDelete(review.id)
		setIsEditing(false)
	}

	const periodLabel = review.year && review.period ? formatPeriodLabel(review.year, review.period) : null

	return (
		<Box sx={{ mt: 1 }} width="100%" marginY={1} borderRadius={1} boxShadow={2}>
			{isEditing ? (
				<Box>
					<ConfirmDialog
						title="Aviso"
						open={isDeleteConfirmOpen}
						setOpen={setIsDeleteConfirmOpen}
						onConfirm={handleDelete}
					>
						Tem certeza que deseja excluir o registro?
					</ConfirmDialog>
					<Box padding={1}>
						<FormControl fullWidth size="small" sx={{ mb: 1 }}>
							<InputLabel>Resultado</InputLabel>
							<Select label="Resultado" value={editPeriod} onChange={(e) => setEditPeriod(e.target.value)}>
								{periodOptions.map(opt => (
									<MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
								))}
							</Select>
						</FormControl>
						<TextField multiline rows={6} fullWidth value={content} onChange={(e) => setContent(e.target.value)} />
					</Box>
					<Box display="flex" alignItems="center" padding={1}>
						<Typography variant="caption" flex={1}>Escrito em {moment(review.date).format('DD/MM/YYYY')}</Typography>
						<Stack direction="row" spacing={1} justifyContent="flex-end">
							<Button variant="contained" color="secondary" onClick={() => setIsDeleteConfirmOpen(true)}><DeleteIcon /></Button>
							<Button variant="contained" onClick={handleSave}>{currentBreakPoint('SIZE') > 2 ? 'Salvar' : <SaveIcon />}</Button>
							<Button variant="contained" onClick={() => setIsEditing(false)}>{currentBreakPoint('SIZE') > 2 ? 'Cancelar' : <CancelIcon />}</Button>
						</Stack>
					</Box>
				</Box>
			) : (
				<Box>
					<Box padding={1}>
						{periodLabel && (
							<Typography variant="subtitle1" fontWeight="bold" gutterBottom>{periodLabel}</Typography>
						)}
						<Typography sx={{ whiteSpace: 'pre-wrap' }}>{content}</Typography>
					</Box>
					<Box display="flex" alignItems="center" padding={1}>
						<Typography variant="caption" flex={1}>Escrito em {moment(review.date).format('DD/MM/YYYY')}</Typography>
						<Button variant="contained" sx={{ width: 100 }} onClick={() => setIsEditing(true)}>Editar</Button>
					</Box>
				</Box>
			)}
		</Box>
	)
}

export default PanelReviews
